import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import AppStoreBadges from "@/components/landing/AppStoreBadges";
import Breadcrumbs from "@/components/blog/Breadcrumbs";
import NextRead from "@/components/blog/NextRead";
import EmbeddedRetirementCalculator from "./EmbeddedRetirementCalculator";
import RetirementStickyBanner from "./RetirementStickyBanner";
import { SPOKE_CONTENT, type SpokeContent } from "./spokeContent";

const Footer = lazy(() => import("@/components/landing/Footer"));

const SITE_URL = "https://getpiefolio.com";

interface Props {
  age: 50 | 55 | 60;
}

const RetireSpokePage = ({ age }: Props) => {
  const content: SpokeContent = SPOKE_CONTENT[age];
  const canonical = `${SITE_URL}/blog/${content.slug}`;
  const others = ([50, 55, 60] as const).filter((a) => a !== age);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: content.title,
    description: content.metaDescription,
    author: { "@type": "Organization", name: "PieFolio AI" },
    publisher: {
      "@type": "Organization",
      name: "PieFolio AI",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
    },
    mainEntityOfPage: canonical,
    url: canonical,
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
      { "@type": "ListItem", position: 3, name: content.title, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{content.metaTitle}</title>
        <meta name="description" content={content.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={content.metaTitle} />
        <meta property="og:description" content={content.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-6xl">
          <div className="pt-4">
            <Breadcrumbs
              items={[
                { label: "Home", to: "/" },
                { label: "Blog", to: "/blog" },
                { label: `Retire at ${age}` },
              ]}
            />
          </div>

          <header className="text-center py-8 sm:py-12 max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4">
              {content.title}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mb-6">
              {content.heroLede}
            </p>
            <div className="flex justify-center">
              <AppStoreBadges
                variant="dark"
                size="md"
                align="center"
                location={`retire_spoke_${age}_hero`}
              />
            </div>
          </header>

          {/* Embedded calculator pre-set to this age */}
          <EmbeddedRetirementCalculator targetAge={age} />

          {/* Plain-text key conclusions — never trapped inside a chart */}
          <section
            aria-labelledby="key-conclusions"
            className="mt-10 rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm"
          >
            <h2
              id="key-conclusions"
              className="text-xl sm:text-2xl font-extrabold tracking-tight mb-4"
            >
              The bottom line for retiring at {age}
            </h2>
            <ul className="space-y-3">
              {content.keyConclusions.map((c, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="shrink-0 mt-1 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-base sm:text-lg font-semibold text-foreground leading-relaxed">
                    {c}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          {/* Deep age-specific sections */}
          <article className="mt-12 max-w-3xl mx-auto space-y-10">
            {content.sections.map((s) => (
              <section
                key={s.id}
                id={s.id}
                className="scroll-mt-28"
                aria-labelledby={`${s.id}-h`}
              >
                <h2
                  id={`${s.id}-h`}
                  className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-4"
                >
                  {s.heading}
                </h2>
                <div className="space-y-4">{s.body}</div>
                <p className="mt-5 rounded-lg border-l-4 border-primary bg-primary/5 px-4 py-3 font-semibold text-foreground">
                  {s.conclusion}
                </p>
              </section>
            ))}
          </article>

          {/* FAQ */}
          <section
            className="mt-16 max-w-3xl mx-auto"
            aria-labelledby="faq-h"
          >
            <h2
              id="faq-h"
              className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-6"
            >
              Frequently asked questions about retiring at {age}
            </h2>
            <div className="space-y-5">
              {content.faqs.map((f) => (
                <details
                  key={f.q}
                  className="group rounded-xl border border-border bg-card p-5"
                >
                  <summary className="cursor-pointer font-bold text-base sm:text-lg list-none flex justify-between items-center">
                    {f.q}
                    <span className="ml-3 text-primary transition-transform group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-foreground/80 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Explore Other Early Retirement Timelines */}
          <section
            aria-labelledby="explore-timelines"
            className="mt-20 mb-12 max-w-5xl mx-auto"
          >
            <div className="text-center mb-8">
              <span className="inline-block text-xs uppercase tracking-[0.18em] text-primary font-bold mb-3">
                Hub & Spoke
              </span>
              <h2
                id="explore-timelines"
                className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-3"
              >
                Explore Other Early Retirement Timelines
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Each age comes with a different bridge to pensions, healthcare,
                and tax-advantaged accounts — here's the deep guide for the other two.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {others.map((a) => {
                const sib = SPOKE_CONTENT[a];
                return (
                  <Link
                    key={a}
                    to={`/blog/${sib.slug}`}
                    className="group relative flex flex-col rounded-2xl border border-border bg-card p-6 sm:p-7 shadow-sm hover:shadow-xl hover:border-primary/60 transition-all overflow-hidden"
                  >
                    <div
                      aria-hidden
                      className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary/10 blur-3xl opacity-60 group-hover:opacity-100 transition-opacity"
                    />
                    <span className="relative inline-flex items-center self-start rounded-full bg-primary/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary mb-4">
                      Age {a}
                    </span>
                    <h3 className="relative text-xl sm:text-2xl font-extrabold tracking-tight mb-2 group-hover:text-primary transition-colors">
                      {sib.title}
                    </h3>
                    <p className="relative text-sm text-muted-foreground leading-relaxed mb-4">
                      {sib.heroLede}
                    </p>
                    <p className="relative text-sm font-semibold text-foreground leading-relaxed border-l-4 border-primary/60 bg-primary/5 rounded-r-md pl-3 pr-2 py-2 mb-5">
                      {sib.keyConclusions[0]}
                    </p>
                    <span className="relative mt-auto inline-flex items-center gap-1.5 text-sm font-bold text-primary">
                      Read the full guide
                      <span className="transition-transform group-hover:translate-x-1">→</span>
                    </span>
                  </Link>
                );
              })}
            </div>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              Or run any age yourself in the{" "}
              <Link
                to="/tools/retirement-calculator"
                className="font-semibold text-primary hover:underline"
              >
                full Retirement Calculator →
              </Link>
            </div>
          </section>

          <NextRead slug={content.slug} />
        </div>
      </main>

      <RetirementStickyBanner location={`retire_at_${age}_sticky`} />

      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default RetireSpokePage;
